// ============================================================
// Aperçu : coupe et rendement, calculés par le vrai générateur
// ============================================================
// Deux nombres par ligne de minerai, sur un échantillon de chunks en roche pleine (tout bloc est
// remplaçable) :
//   - « appels » : ce que la page Java appelle « Rendement par minerai », chaque appel du générateur,
//     doublons compris (deux billes d'un filon qui se recouvrent comptent deux fois) ;
//   - « posés » : les blocs réellement posés. Le mod ne remplace qu'une roche hôte : un bloc déjà
//     devenu minerai ne l'est plus, le premier minerai qui l'atteint le garde. C'est ce nombre qui
//     dit combien de minerai on trouvera, en roche pleine.
// Sur une vraie carte, c'est moins : grottes, eau, surface, blocs posés par les joueurs (sur la
// farmland du 24/10 : 272 blocs posés par chunk en moyenne pour 1 287 en roche pleine). Le mod écrit
// les vrais nombres dans son journal à la fin de chaque passe.
// ============================================================

import { WORLD_CEILING_Y, WORLD_FLOOR_Y, generateChunk, type GenConfig, type GenOre } from './oreGenerator.ts';

export const WORLD_HEIGHT = WORLD_CEILING_Y - WORLD_FLOOR_Y + 1;

export interface LineYield {
  ore: GenOre;
  /** Rang dans config.activeOres (couleur, coupe). */
  index: number;
  /** Blocs réellement posés par chunk, en roche pleine. */
  placed: number;
  /** Appels du générateur par chunk (le « Rendement par minerai » de la page Java). */
  calls: number;
}

export interface Simulation {
  /** Chunks réellement rejoués (moins que l'échantillon si le budget d'appels a été atteint). */
  chunks: number;
  /** Échantillon écourté : le budget d'appels du générateur a été atteint (filons énormes). */
  partial: boolean;
  yields: LineYield[];
  totalPlaced: number;
  totalCalls: number;
  /** Coupe : rang de la ligne (config.activeOres) par case, -1 pour la roche. Ligne 0 = WORLD_CEILING_Y. */
  slice: Int16Array;
  sliceWidth: number;
  /** Blocs posés par chunk, par altitude (indice y - WORLD_FLOOR_Y). */
  byY: Float64Array;
}

export interface SampleOptions {
  /** Largeur de l'échantillon (et de la coupe), en chunks. */
  width: number;
  /** Profondeur de l'échantillon, en chunks ; la coupe est dans la première rangée. */
  depth: number;
  /** Premier chunk de l'échantillon. */
  originX: number;
  originZ: number;
  /** Z de la coupe dans la première rangée de chunks, de 0 à 15. */
  sliceZ: number;
}

export const DEFAULT_SAMPLE: SampleOptions = { width: 16, depth: 2, originX: 0, originZ: 0, sliceZ: 8 };

/** Au-delà de ce nombre d'appels du générateur, l'échantillon s'arrête au chunk en cours (quelques secondes de calcul). */
export const DEFAULT_MAX_CALLS = 40_000_000;

/** Rejoue l'échantillon : rendement par ligne et coupe verticale. */
export function simulate(config: GenConfig, sample: SampleOptions = DEFAULT_SAMPLE, maxCalls = DEFAULT_MAX_CALLS): Simulation {
  const indexOf = new Map<GenOre, number>();
  config.activeOres.forEach((ore, i) => indexOf.set(ore, i));
  const placed = new Float64Array(config.activeOres.length);
  const calls = new Float64Array(config.activeOres.length);
  const byY = new Float64Array(WORLD_HEIGHT);
  const sliceWidth = sample.width * 16;
  const slice = new Int16Array(sliceWidth * WORLD_HEIGHT).fill(-1);
  // Premier minerai posé à chaque case du chunk visé (16 × 16 × 384), -1 : roche.
  const cells = new Int16Array(16 * 16 * WORLD_HEIGHT);
  const sliceZ = (sample.originZ << 4) + sample.sliceZ;
  let done = 0;
  let totalCalls = 0;

  outer:
  for (let cz = 0; cz < sample.depth; cz += 1) {
    for (let cx = 0; cx < sample.width; cx += 1) {
      if (totalCalls > maxCalls) break outer;
      cells.fill(-1);
      const chunkX = sample.originX + cx;
      const chunkZ = sample.originZ + cz;
      generateChunk(config, chunkX, chunkZ, (x, y, z, ore) => {
        const i = indexOf.get(ore);
        if (i === undefined) return;
        calls[i] += 1;
        totalCalls += 1;
        const cell = ((y - WORLD_FLOOR_Y) * 16 + (z & 15)) * 16 + (x & 15);
        if (cells[cell] !== -1) return;
        cells[cell] = i;
        placed[i] += 1;
        byY[y - WORLD_FLOOR_Y] += 1;
        if (z === sliceZ) {
          const column = x - (sample.originX << 4);
          if (column >= 0 && column < sliceWidth) slice[(WORLD_CEILING_Y - y) * sliceWidth + column] = i;
        }
      });
      done += 1;
    }
  }

  const chunks = Math.max(1, done);
  for (let y = 0; y < WORLD_HEIGHT; y += 1) byY[y] /= chunks;
  const yields = config.activeOres.map((ore, index) => ({ ore, index, placed: placed[index] / chunks, calls: calls[index] / chunks }));
  return {
    chunks,
    partial: done < sample.width * sample.depth,
    yields,
    totalPlaced: yields.reduce((a, y) => a + y.placed, 0),
    totalCalls: yields.reduce((a, y) => a + y.calls, 0),
    slice,
    sliceWidth,
    byY,
  };
}

/** Nombre moyen d'amas dans un chunk qui a une grotte (PerChunk : probabilité de 0, 1, 2... amas). */
export function meanClusters(perChunk: number[]): number {
  return perChunk.reduce((sum, p, k) => sum + Math.max(0, p) * k, 0);
}

// ==================== Couleurs ====================

// Couleurs choisies pour se distinguer sur la roche assombrie de la coupe (le charbon y est presque noir).
const KNOWN: Record<string, string> = {
  coal: '#050505',
  iron: '#d8af93',
  copper: '#e0773f',
  gold: '#f5d33b',
  redstone: '#e3241b',
  lapis: '#2f5fd0',
  diamond: '#4ee6dd',
  emerald: '#2fd06b',
  tin: '#7f9c8a',
  aluminium: '#9fc3ff',
  aluminum: '#9fc3ff',
  silicon: '#8a6bd1',
  uranium: '#9be22d',
  marble: '#fbf8f1',
  quartz: '#f2d9c4',
};

/** Couleur d'un bloc : d'après son nom (coal, iron...) sinon tirée de l'identifiant, stable. */
export function blockColor(block: string): string {
  const name = block.toLowerCase();
  for (const [key, color] of Object.entries(KNOWN)) if (name.includes(key)) return color;
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 70% 60%)`;
}

/** Couleur de la roche d'une couche (fond de la coupe). */
export function rockColor(block: string): string {
  const name = block.toLowerCase();
  if (name.includes('deepslate')) return '#4a4a52';
  if (name.includes('netherrack')) return '#6b2c2c';
  if (name.includes('end_stone')) return '#d9d79c';
  if (name.includes('sandstone')) return '#d8c88f';
  return '#8b8b8b';
}
